import React from 'react';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import { Skeleton } from '@dashboard/components/ui/skeleton';

// Rows per actions group, as each page's panel has them: a customer has Customer and Stripe.
const ACTION_GROUPS = { order: [2, 2, 2], customer: [2, 1], coupon: [2, 2, 2] };

export const SkeletonCard = ({ titleWidth = 'w-24', children }) => (
  <Card className="mx-auto">
    <CardContent>
      <Skeleton className={`mb-3 h-6 ${titleWidth}`} />
      {children}
    </CardContent>
  </Card>
);

export const SkeletonTableRows = ({ count }) => (
  <div className="divide-y divide-gray-200 dark:divide-border rounded-lg border border-gray-200 dark:border-border">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="flex items-center justify-between gap-4 px-4 py-3.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-36" />
      </div>
    ))}
  </div>
);

const OrderBody = () => (
  <>
    <SkeletonCard>
      <Skeleton className="h-36 w-full rounded-lg" />
    </SkeletonCard>
    <SkeletonCard>
      <Skeleton className="h-16 w-full rounded-lg" />
    </SkeletonCard>
    <SkeletonCard titleWidth="w-16">
      <SkeletonTableRows count={4} />
    </SkeletonCard>
  </>
);

const CustomerBody = () => (
  <SkeletonCard titleWidth="w-32">
    <SkeletonTableRows count={10} />
  </SkeletonCard>
);

const CouponBody = () => (
  <>
    <div className="grid grid-cols-2 gap-2 px-4 pt-4 md:grid-cols-4">
      {[0, 1, 2, 3].map(index => (
        <div
          key={index}
          className="space-y-2 rounded-lg border border-gray-200 dark:border-border p-4"
        >
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-10" />
        </div>
      ))}
    </div>
    <SkeletonCard titleWidth="w-16">
      <SkeletonTableRows count={6} />
    </SkeletonCard>
  </>
);

const ListBody = () => (
  <SkeletonCard titleWidth="w-28">
    <div className="divide-y divide-gray-200 dark:divide-border rounded-md border border-gray-200 dark:border-border">
      {Array.from({ length: 4 }).map((_, row) => (
        <div key={row} className="flex gap-4 px-4 py-3.5">
          {Array.from({ length: 5 }).map((__, column) => (
            <Skeleton key={column} className="h-4 flex-1 max-w-[200px]" />
          ))}
        </div>
      ))}
    </div>
  </SkeletonCard>
);

const AttendeesBody = () => (
  <>
    <SkeletonCard titleWidth="w-40">
      <SkeletonTableRows count={2} />
    </SkeletonCard>
    <SkeletonCard>
      <SkeletonTableRows count={3} />
    </SkeletonCard>
  </>
);

const BODIES = {
  order: OrderBody,
  customer: CustomerBody,
  coupon: CouponBody,
  list: ListBody,
  attendees: AttendeesBody,
};

/** An order page refreshed on one of its tabs loads in that tab's shape: no actions column. */
export const orderTabSkeleton = tab =>
  tab
    ? {
        actions: false,
        variant: ['attendees', 'group-registration', 'tickets'].includes(tab) ? 'attendees' : 'list',
      }
    : {};

/**
 * The detail pages' loading state, in their own shape: title, date and tabs, then the page's
 * cards and its actions column. `variant` picks the body: order, customer, coupon, list (a tab
 * that is one table) or attendees.
 */
export default function OrderPageSkeleton({ tabs = 4, actions = true, variant = 'order' }) {
  const Body = BODIES[variant];
  return (
    <Card variant="root" contentColumn aria-busy="true" aria-label="Loading">
      <CardHeader variant="root">
        <div className="w-full space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-[23px] w-48" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-36" />
          {variant === 'customer' && <Skeleton className="h-4 w-44" />}
          <div className="flex gap-2 pt-2">
            {Array.from({ length: tabs }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-20" />
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent variant="root">
        <div
          className={`flex flex-col lg:flex-row items-start justify-center ${variant === 'coupon' ? 'max-w-3xl' : 'max-w-5xl'}`}
        >
          <div className="order-2 lg:order-1 w-full lg:pr-8">
            <Body />
          </div>
          {actions && (
            <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
              <div className="flex flex-col divide-y divide-gray-200 dark:divide-border rounded-lg border border-gray-200 dark:border-border px-4">
                {(ACTION_GROUPS[variant] ?? ACTION_GROUPS.order).map((rows, group) => (
                  <div key={group} className="space-y-3 py-4">
                    <Skeleton className="h-4 w-16" />
                    {Array.from({ length: rows }).map((_, row) => (
                      <Skeleton key={row} className={`h-4 ${row % 2 ? 'w-28' : 'w-32'}`} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
